/* eslint-disable prefer-const */
import { Col, Form, Input, Row, Select, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { useGetInfoByUserCode } from '../../../../../api/profile'
import { ReactComponent as CheckSelectIcon } from '../../../../../common/images/svg/CheckSelect.svg'
import { ReactComponent as ArrowIcon } from '../../../../../common/images/svg/SuffixSelectIcon.svg'
import { CustomButton } from '../../../../../components/button/buttonCustom'
import { SmartUidAutoCompleteInput } from '../../../../../components/inputs/autocomplete/smartUidAutocomplete'
import { companyRegistrationNumberRules } from '../../../../../contants/addCompany.constants'
import { ButtonTransferMode } from '../../../../../pages/home/component/button-transfer-mode'
import {
  addressRules,
  cityRules,
  clearFieldErrorMessageOnChange,
  companyNameRules,
  directorNameRules,
  emailRules,
  nameRules,
  onlyLatinCharsDigitsRule,
  onlyLatinCharsDigitsSpecialSymbolsRule,
  phoneNumberRules,
  postalCodeRules,
  tinRules,
  websiteRules,
} from '../../../../../utils/form.constants'
import useResponsive from '../../../../../utils/hooks/useResponsive'
import { InfoBeneficiary } from '../../../type'

const { TextArea } = Input

interface IFormInfoProps {
  onSubmitInfo: (values: InfoBeneficiary) => void
  stepNewBeneficiary: number | null
  metadataCountries: { value: string; label: string }[]
  valueFormInfo: InfoBeneficiary | undefined
  setInfoByUserCode: React.Dispatch<any>
  infoByUserCode: any
}

const requiredFields = [
  'companyName',
  'nickName',
  'directorFirstName',
  'directorLastName',
  'address1',
  'city',
  'stateProvince',
  'companyOfficiallyRegistered',
  'tinVatUid',
  'regNo',
  'phoneNumber1',
  'email',
  'website',
  'postalCode',
]

const FormInfo = ({
  onSubmitInfo,
  stepNewBeneficiary,
  metadataCountries,
  valueFormInfo,
  setInfoByUserCode,
  infoByUserCode,
}: IFormInfoProps) => {
  const { isTablet } = useResponsive()
  const [form] = Form.useForm<InfoBeneficiary>()
  const [searchCountry, setSearchCountry] = useState<string>('')
  const [isAllFieldsFilled, setIsAllFieldsFilled] = useState(false)
  const [beneficiaryType, setBeneficiaryType] = useState('NEW')
  const [disableField, setDisableField] = useState(false)

  const { mutate: fetchInfoByUserCode, isLoading } = useGetInfoByUserCode({
    onSuccess: (response: { data: { data: any } }) => {
      setInfoByUserCode(response?.data?.data)
      form.setFields([{ name: 'smartUID', errors: undefined }])
    },
    onError: (error: { message: string; reason_code: string }) => {
      setInfoByUserCode(
        error?.reason_code === 'ERR00010'
          ? 'This SMART UID does not exist in SMART+'
          : error?.message
      )
      if (error?.reason_code === 'ERR00010') {
        form.setFields([
          {
            name: 'smartUID',
            errors: ['This SMART UID does not exist in SMART+'],
          },
        ])
      } else {
        form.setFields([{ name: 'smartUID', errors: [error?.message || ''] }])
      }
    },
  })

  const checkAllFieldsFilled = () => {
    const values = form.getFieldsValue(requiredFields as any)
    return requiredFields.every((field) => !!values[field])
  }

  const handleChangeCountry = (value: string) => {
    setSearchCountry(value)
  }

  useEffect(() => {
    if (!valueFormInfo) return
    form.setFieldsValue({
      companyName: valueFormInfo.companyName,
      nickName: valueFormInfo.nickName,
      directorFirstName: valueFormInfo.directorFirstName,
      directorLastName: valueFormInfo.directorLastName,
      address1: valueFormInfo.address1,
      city: valueFormInfo.city,
      stateProvince: valueFormInfo.stateProvince,
      companyOfficiallyRegistered: valueFormInfo.companyOfficiallyRegistered,
      tinVatUid: valueFormInfo.tinVatUid,
      regNo: valueFormInfo.regNo,
      phoneNumber1: valueFormInfo.phoneNumber1,
      email: valueFormInfo.email,
      smartUID: valueFormInfo.smartUID,
      website: valueFormInfo.website,
      postalCode: valueFormInfo.postalCode,
    })
  }, [valueFormInfo])

  useEffect(() => {
    if (
      !infoByUserCode ||
      typeof infoByUserCode === 'string' ||
      beneficiaryType === 'NEW'
    ) {
      setDisableField(false)
      return
    }
    setDisableField(true)
    form.setFieldsValue({
      companyName: infoByUserCode?.fullName,
      address1: infoByUserCode?.address,
      companyOfficiallyRegistered: infoByUserCode?.country,
      regNo: infoByUserCode?.regNo,
      email: infoByUserCode?.email,
      website: infoByUserCode?.website,
    })
  }, [infoByUserCode, beneficiaryType])

  const resetFieldsAfterSmartUidChange = () => {
    form?.resetFields([
      'companyName',
      'nickName',
      'directorFirstName',
      'directorLastName',
      'address1',
      'address2',
      'city',
      'stateProvince',
      'companyOfficiallyRegistered',
      'tinVatUid',
      'regNo',
      'phoneNumber1',
      'email',
      'website',
      'postalCode',
    ])
  }

  const fetchProfileBySmartUid = async (rawCode: string) => {
    const code = rawCode?.trim() ?? ''
    resetFieldsAfterSmartUidChange()
    await fetchInfoByUserCode(code)
  }

  const handleBlur = async () => {
    const code = form.getFieldValue('smartUID')
      ? form.getFieldValue('smartUID')?.trim()
      : ''

    if (!code) {
      setDisableField(false)
      resetFieldsAfterSmartUidChange()
      form.setFields([{ name: 'smartUID', errors: undefined }])
      setInfoByUserCode(null)
      return
    }
    await fetchProfileBySmartUid(code)
  }

  const handleChangeMethod = (type: string) => {
    setDisableField(false)
    setBeneficiaryType(type)
    setInfoByUserCode(null)
    form?.resetFields()
  }

  return (
    <Form
      style={{ display: stepNewBeneficiary === 1 ? 'block' : 'none' }}
      form={form}
      onFinish={(value) => {
        onSubmitInfo(value)
        form.resetFields()
      }}
      onValuesChange={() => {
        clearFieldErrorMessageOnChange(form)
        setIsAllFieldsFilled(checkAllFieldsFilled())
      }}>
      <div
        className='form-info-content'
        style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.15)' }}>
        <div className='transfer-mode-list-button'>
          <ButtonTransferMode
            size={isTablet ? 'small' : 'medium'}
            active={beneficiaryType === 'NEW'}
            text='New'
            width={'60px'}
            onClick={() => handleChangeMethod('NEW')}
          />
          <ButtonTransferMode
            size={isTablet ? 'small' : 'medium'}
            active={beneficiaryType === 'FIND_ON_SMART'}
            text='Find on SMART'
            width={'fit-content'}
            onClick={() => handleChangeMethod('FIND_ON_SMART')}
          />
        </div>
        {beneficiaryType === 'FIND_ON_SMART' ? (
          <Col style={{ marginTop: '12px' }} xs={24}>
            <Form.Item
              label='SMART UID'
              validateTrigger='onBlur'
              name='smartUID'
              rules={[
                { required: true, message: 'This field is required', whitespace: true },
                {
                  max: 256,
                  message: 'Maximum 256 characters',
                  validateTrigger: 'onBlur',
                },
              ]}>
              <SmartUidAutoCompleteInput
                placeholder='Enter the code linked to your SMART account (min. 3 characters)'
                onBlur={handleBlur}
                onSuggestionSelect={(uid) => fetchProfileBySmartUid(uid)}
              />
            </Form.Item>
          </Col>
        ) : null}
      </div>
      <div className='form-info-content'>
        <Row gutter={{ xs: 8, sm: 30, md: isTablet ? 40 : 60 }}>
          <Col
            style={{ marginBottom: isTablet ? '18px' : '24px' }}
            xs={24}
            md={12}>
            <Form.Item
              label='Company Name'
              name='companyName'
              validateTrigger='onBlur'
              rules={companyNameRules}>
              <Input
                readOnly
                onFocus={(e) => e.currentTarget.removeAttribute('readonly')}
                placeholder='Enter the full legal name of the company'
                disabled={disableField}
              />
            </Form.Item>
          </Col>
          <Col
            style={{ marginBottom: isTablet ? '18px' : '24px' }}
            xs={24}
            md={12}>
            <Form.Item
              label='Nickname'
              name='nickName'
              validateTrigger='onBlur'
              rules={[...nameRules, onlyLatinCharsDigitsRule]}>
              <Input
                readOnly
                onFocus={(e) => e.currentTarget.removeAttribute('readonly')}
                placeholder='Enter a short name or alias for internal use'
              />
            </Form.Item>
          </Col>
          <Col
            style={{ marginBottom: isTablet ? '18px' : '24px' }}
            xs={24}
            md={12}>
            <Form.Item
              label='Reg No'
              name='regNo'
              validateTrigger='onBlur'
              rules={companyRegistrationNumberRules}>
              <Input
                disabled={disableField}
                placeholder='Enter the official company registration number'
              />
            </Form.Item>
          </Col>
          <Col
            style={{ marginBottom: isTablet ? '18px' : '24px' }}
            xs={24}
            md={12}>
            <Form.Item
              label='TIN'
              name='tinVatUid'
              validateTrigger='onBlur'
              rules={tinRules}>
              <Input placeholder='Enter the company’s Tax Identification Number' />
            </Form.Item>
          </Col>

          <Col
            style={{ marginBottom: isTablet ? '18px' : '24px' }}
            xs={24}
            md={12}>
            <Form.Item
              label='Address 1'
              name='address1'
              validateTrigger='onBlur'
              rules={addressRules}>
              <TextArea
                disabled={disableField}
                className='beneficiary-address'
                autoSize={{ minRows: 1, maxRows: 5 }}
                placeholder='Enter the primary street address of the company'
              />
            </Form.Item>
          </Col>
          <Col
            style={{ marginBottom: isTablet ? '18px' : '24px' }}
            xs={24}
            md={12}>
            <Form.Item
              label='Address 2'
              name='address2'
              validateTrigger='onBlur'
              rules={[
                onlyLatinCharsDigitsSpecialSymbolsRule,
                {
                  max: 256,
                  message: 'Maximum 256 characters',
                  validateTrigger: 'onBlur',
                },
              ]}>
              <TextArea
                className='beneficiary-address'
                autoSize={{ minRows: 1, maxRows: 5 }}
                placeholder='Additional address details (e.g., building, floor)'
              />
            </Form.Item>
          </Col>
          <Col
            style={{ marginBottom: isTablet ? '18px' : '24px' }}
            xs={24}
            md={12}>
            <Form.Item
              label='City'
              name='city'
              validateTrigger='onBlur'
              rules={cityRules}>
              <Input placeholder='Enter the city where the company is located' />
            </Form.Item>
          </Col>
          <Col
            style={{ marginBottom: isTablet ? '18px' : '24px' }}
            xs={24}
            md={12}>
            <Form.Item
              label='State/Province'
              name='stateProvince'
              validateTrigger='onBlur'
              rules={cityRules}>
              <Input placeholder='Enter the state or province of company location' />
            </Form.Item>
          </Col>
          <Col
            style={{ marginBottom: isTablet ? '18px' : '24px' }}
            xs={24}
            md={12}>
            <Form.Item
              label='Country of incorporation'
              name='companyOfficiallyRegistered'
              validateTrigger='onBlur'
              rules={[{ required: true, message: 'This field is required' }]}>
              <Select
                disabled={disableField}
                aria-autocomplete='none'
                showSearch
                placeholder='Select the country where the company is officially registered'
                suffixIcon={<ArrowIcon />}
                onChange={handleChangeCountry}
                optionLabelProp='label'
                className='select-country-beneficiary'
                filterOption={(input, option) => {
                  const label = option?.label
                  if (typeof label === 'string') {
                    return label.toLowerCase().includes(input.toLowerCase())
                  }
                  return false
                }}>
                {metadataCountries.map((item) => (
                  <Select.Option
                    key={item.value}
                    value={item.value}
                    label={item.label}>
                    <span
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 2,
                      }}>
                      <span
                        className='ant-select-custom-value '
                        style={{ whiteSpace: 'nowrap' }}>
                        {item.label}
                      </span>
                      {searchCountry === item.value && (
                        <CheckSelectIcon style={{ flexShrink: 0 }} />
                      )}
                    </span>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label='Postal Code'
              name='postalCode'
              validateTrigger='onBlur'
              rules={postalCodeRules}>
              <Input placeholder='Enter your postal code' />
            </Form.Item>
          </Col>
          <Col
            style={{ marginBottom: isTablet ? '18px' : '24px' }}
            xs={24}
            md={12}>
            <Form.Item
              label="Director's First Name"
              name='directorFirstName'
              validateTrigger='onBlur'
              rules={directorNameRules}>
              <Input
                readOnly
                onFocus={(e) => e.currentTarget.removeAttribute('readonly')}
                placeholder='Enter the first name(s) of the company director'
              />
            </Form.Item>
          </Col>
          <Col
            style={{ marginBottom: isTablet ? '18px' : '24px' }}
            xs={24}
            md={12}>
            <Form.Item
              label="Director's Last Name"
              name='directorLastName'
              validateTrigger='onBlur'
              rules={directorNameRules}>
              <Input
                readOnly
                onFocus={(e) => e.currentTarget.removeAttribute('readonly')}
                placeholder='Enter the last name (surname) of the company director'
              />
            </Form.Item>
          </Col>
          <Col
            style={{ marginBottom: isTablet ? '18px' : '24px' }}
            xs={24}
            md={12}>
            <Form.Item
              label='Tel'
              name='phoneNumber1'
              validateTrigger='onBlur'
              rules={phoneNumberRules}>
              <Input placeholder='Enter the official company phone number' />
            </Form.Item>
          </Col>
          <Col
            style={{ marginBottom: isTablet ? '18px' : '24px' }}
            xs={24}
            md={12}>
            <Form.Item
              label='Email'
              name='email'
              validateTrigger='onBlur'
              rules={[
                ...emailRules,
                {
                  required: true,
                  message: 'This field is required',
                  validateTrigger: 'onBlur',
                },
              ]}>
              <Input
                disabled={disableField}
                placeholder='Enter the official company email address'
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label='Website'
              name='website'
              validateTrigger='onBlur'
              rules={websiteRules}>
              <Input
                disabled={disableField}
                placeholder="Enter the company's official website URL"
              />
            </Form.Item>
          </Col>
        </Row>
      </div>
      <div className='form-info-footer'>
        <CustomButton
          type='submit'
          text={'Continue'}
          size={isTablet ? 'medium' : 'large'}
          disabled={
            isLoading
            //  ||
            // !isAllFieldsFilled ||
            // form.getFieldError('smartUID')?.length > 0 ||
            // (beneficiaryType === 'FIND_ON_SMART' && !infoByUserCode)
          }
        />
      </div>
    </Form>
  )
}

export default FormInfo
